import { useState } from 'react';
import { useNavigate } from 'react-router';

import { formatClock, formatReservationDay, MISSING } from 'common/helpers/format';
import { useActions } from 'common/hooks/useActions';
import * as T from 'common/types';
import DetailGrid, { DetailItem } from 'components/detailGrid';
import DangerButton from 'components/form/buttons/danger';
import { OutlineButton } from 'components/form/buttons/outLineButton';
import PanelBoxLayout from 'components/layout/panelBox';
import ModalApp from 'components/modalApp';
import QuestionModal from 'components/modal/question';
import Person from 'components/person';
import StatusChip from 'components/statusChip';
import Value from 'components/value';

import ChangeDateTimeForm from '../changeTimeForm';
import * as S from './styles';

interface IProps {
  data: T.Pages.ReservationDetail;
  fetchData: () => void;
}

export default function ReservationDetailInfo({ data, fetchData }: IProps) {
  const { reservationCancelRequestAction } = useActions();
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);
  const [showTimeModal, setShowTimeModal] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleShowCloseTicket = () => {
    setShowCloseModal(true);
  };

  const handleHideCancel = () => {
    setShowCloseModal(false);
  };

  const handleShowTimeModal = () => {
    setShowTimeModal(true);
  };

  const handleCloseTimeModal = () => {
    setShowTimeModal(false);
  };

  const handleCancelReservation = () => {
    reservationCancelRequestAction(data.id, () => {
      handleHideCancel();
      navigate('/panel/reservation');
    });
  };

  const isCanceled = data?.status === 'canceled';
  const people = data?.people || [];

  /*
   * The date and the times both come from the reservation's own local fields
   * — see `formatReservationDay`. The detail payload also carries `startDate`
   * as a UTC instant, and mixing the two is what would make this screen
   * disagree with itself: `startDate` is 13:00Z where `start_time` is 16:00,
   * because the times are office wall-clock and the instant is not.
   */
  const day = formatReservationDay(data?.day, data?.month, data?.year);
  const start = formatClock(data?.start_time);
  const end = formatClock(data?.end_time);

  /*
   * Both branches of this used to hardcode their value — `true` for a
   * completed reservation and `false` for a canceled one — instead of reading
   * the field. Two of the five reservations in the dev data are canceled with
   * `confirmed: true`, and the screen called them Unconfirmed.
   *
   * It reads the field now, and only where the answer means something: on a
   * canceled reservation the outcome is the status, and whether it had been
   * confirmed first is a detail the badge would only muddle.
   */
  const confirmation = isCanceled ? null : <StatusChip domain="confirmation" value={data?.confirmed} />;

  return (
    <S.Content>
      <PanelBoxLayout
        className="flush"
        contentStyle={{ padding: 0 }}
        title={data?.subject || 'Reservation'}
        afterTitle={
          <S.HeadChips>
            <StatusChip domain="reservation" value={data?.status} fallback={<Value />} />
            {confirmation}
          </S.HeadChips>
        }
      >
        <DetailGrid>
          <DetailItem label="Room">
            <Value fallback={MISSING.assigned}>{data?.room?.name}</Value>
          </DetailItem>

          {/*
           * One date, not two. Every reservation in the payload starts and
           * ends on the same day — the API models it that way, sending a
           * single day/month/year — so the old Start Date / End Date pair
           * printed the identical value twice on every record.
           */}
          <DetailItem label="Date">
            <S.DateCell>
              <Value mono>{day}</Value>
              {data?.weekend_day ? <S.WeekDay>{data.weekend_day}</S.WeekDay> : <></>}
            </S.DateCell>
          </DetailItem>

          <DetailItem label="Time">
            <Value mono>{start && end ? `${start} – ${end}` : ''}</Value>
          </DetailItem>
        </DetailGrid>

        <S.Body>
          <S.SubHead>{`People · ${people.length}`}</S.SubHead>
          {people.length ? (
            <S.People>
              {people.map((item: any) => (
                <Person
                  key={item?.id}
                  name={item?.name}
                  src={item?.profilePicture}
                  meta={[item?.mail, item?.jobTitle].filter(Boolean).join(' · ')}
                  badge={<StatusChip domain="reservationRole" value={item?.label} />}
                />
              ))}
            </S.People>
          ) : (
            <S.NoPeople>No one is on this reservation.</S.NoPeople>
          )}
        </S.Body>

        {/*
         * The design's action bar: the ordinary action at one end, the
         * destructive one at the other, on the card's own foot rather than
         * loose in the body. Both were disabled on a canceled reservation
         * before and still are — there is nothing left to change or cancel.
         */}
        <S.ActionBar>
          <OutlineButton onClick={handleShowTimeModal} disabled={isCanceled} className="sm">
            Change date and time
          </OutlineButton>
          <S.Spacer />
          <DangerButton onClick={handleShowCloseTicket} disabled={isCanceled} className="sm">
            Cancel reservation
          </DangerButton>
        </S.ActionBar>
      </PanelBoxLayout>

      <QuestionModal
        show={showCloseModal}
        handleClose={handleHideCancel}
        model="delete"
        title="Cancel Reservation"
        text="Do you want to Cancel this Reservation?"
        handleSuccess={handleCancelReservation}
        successButtonText="Yes"
      />

      <ModalApp show={showTimeModal} handleClose={handleCloseTimeModal} fixed style={{ width: 520 }}>
        <ChangeDateTimeForm data={data} handleClose={handleCloseTimeModal} fetchData={() => fetchData()} />
      </ModalApp>
    </S.Content>
  );
}
